import { ODataObject } from '../../interfaces/odata-model.interface';
import {
    JsonMultilineTextModelOptions,
    MultilineTextModelOptions,
    MultilineTextModelOptionsFromJson,
} from '../options/multiline-text-model-options';
import { PrimitiveModel } from './primitive.model';
import { TextInputUtilities } from '../../utilities/text-input-utilities';

export class MultilineTextModel extends PrimitiveModel {
    declare public value: string;
    public constructor(
        name: string,
        value = '',
        public options = new MultilineTextModelOptions(),
    ) {
        super(name, value, options);

        // if format is required then text will be formatted when model loads
        // use case: trouble text in AA data which read from NDP and should be formatted
        if (this.options.formatMultilineTextPattern) {
            this.value = this.getFormattedText();
        }
    }

    /**
     * The lines has to be auto formatted using the modelOptions.lineLength value
     * This returns the string result of formatted lines
     *
     * @returns string
     */
    public getFormattedText(): string {
        const lines = this.getLines();
        // return the line array as string joined with new line
        return lines.join('\n');
    }

    /**
     * get the lines array created using modelOptions.lineLength value
     *
     * @param forOutModel for outmodel we need to append [url] tags before sending it to backend
     * @returns string
     */
    private getLines(forOutModel = false): string[] {
        let lines: string[] = [];
        let regex = new RegExp(`.{1,${this.options.lineLength}}`, 'g');

        if (this.options.formatMultilineTextPattern) {
            // https://www.regextester.com/?fam=111048
            // regular expression allow the string to break without breaking the word if whitespace exists
            // if there is no whitespace found the word will break
            // pattern is controlled from GW to allow different pattern based on countries
            regex = new RegExp(this.options.formatMultilineTextPattern, 'g');
        }

        // split the text value by line breaks
        if (this.value && this.value.length > 0) {
            lines = this.value.split('\n');

            lines = lines.flatMap((line) => {
                // retain empty line breaks added by the user
                if (line.length === 0) {
                    return [''];
                }
                const parts = TextInputUtilities.splitLineByUrls(line);

                return parts.flatMap((part) => {
                    if (part.match(TextInputUtilities.URL_REGEX)) {
                        if (forOutModel) {
                            part = TextInputUtilities.wrapUrlsInText(part);
                            return part.match(new RegExp(`.{1,${this.options.lineLength}}`, 'g')) || [''];
                        } else {
                            return part;
                        }
                    }

                    return part.match(regex) || [''];
                });
            });
        }
        return lines;
    }

    public trimLines(): void {
        if (this.value && this.value.length > 0) {
            const lines = this.value.split('\n');
            this.value = lines.map((line) => line.trim()).join('\n');
        }
    }

    /**
     * Convert the internal data into an object suitable for OData exchange with SAP
     *
     * @returns {object} the internal value in a format suitable for SAP
     */
    public getOutModel(): ODataMultilineTextOutModel {
        const outModel: ODataMultilineTextOutModel = {};

        outModel[this.name] = this.getLines(true).map((line, i) => {
            return {
                Counter: i + 1,
                TextLine: line,
            };
        });

        return outModel;
    }
}

export class MultilineTextModelFromJson extends MultilineTextModel {
    public constructor(data: JsonMultilineTextModel) {
        super(data.name, data.value, new MultilineTextModelOptionsFromJson(data.options));
    }
}

export interface JsonMultilineTextModel {
    name: string;
    value: string;
    options: JsonMultilineTextModelOptions;
}

export interface ODataTextLineOutModel extends ODataObject {
    Counter: number;
    TextLine: string;
}

export interface ODataMultilineTextOutModel extends ODataObject {
    [key: string]: ODataTextLineOutModel[];
}
